import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import rtoAPI from "../api/rtoAPI";
import { buildDeliveredSaleRows, getSaleDateKey, getTotalSalesUnit, normalizeSaleStatus, toSaleNumber } from "../utils/salesDisplay";
import "./TotalSales.css";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  "https://techiohisab.com/api";

const SALES_API = `${API_BASE}/sales`;
const TOTAL_SALES_PAGE_SIZE = 50;

const getTodayDate = () => new Date().toISOString().slice(0, 10);

const toNumber = toSaleNumber;

const getDateKey = getSaleDateKey;

const formatDate = (value) => {
  const dateKey = getDateKey(value);
  if (!dateKey) return "-";

  const [year, month, day] = dateKey.split("-");
  if (!year || !month || !day) return dateKey;

  return `${day}-${month}-${year.slice(-2)}`;
};

const normalizeStatus = normalizeSaleStatus;

const getStatusClass = (status) => {
  switch (normalizeStatus(status)) {
    case "Delivered":
      return "total-sales-status total-sales-status-green";
    case "RTO":
    case "DTO":
      return "total-sales-status total-sales-status-orange";
    default:
      return "total-sales-status total-sales-status-gray";
  }
};

const getRtoAmount = (order) =>
  toNumber(order?.value || order?.amount || order?.total_amount || order?.price);

const getRtoQty = (order) => {
  const qty = toNumber(order?.qty || order?.quantity || order?.total_unit);
  return qty > 0 ? qty : 1;
};

const joinUnique = (values) => {
  const unique = Array.from(
    new Set(values.map((value) => String(value || "").trim()).filter(Boolean))
  );

  if (unique.length === 0) return "-";
  if (unique.length === 1) return unique[0];
  return "Multiple";
};

const isDateInRange = (dateValue, fromDate, toDate) => {
  const dateKey = getDateKey(dateValue);
  if (!dateKey) return false;
  if (fromDate && dateKey < fromDate) return false;
  if (toDate && dateKey > toDate) return false;
  return true;
};

const buildRtoRow = (order, type, index) => ({
  id: `rto-${type}-${order?.id || order?.orderId || index}`,
  date:
    order?.dispatchDate ||
    order?.dispatch_date ||
    order?.returnDate ||
    order?.return_date ||
    order?.date ||
    order?.scannedAt ||
    order?.scanned_at ||
    order?.updatedAt ||
    order?.updated_at ||
    order?.createdAt ||
    order?.created_at,
  platform: order?.platform || order?.store || "Unknown",
  account: order?.accountName || order?.account_name || order?.customerName || order?.customer_name || "-",
  product: order?.productName || order?.product || "-",
        unit: getRtoQty(order),
        productUnit: getRtoQty(order),
  amount: getRtoAmount(order),
  status: type,
});

export default function TotalSales() {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [rtoRows, setRtoRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterFromDate, setFilterFromDate] = useState(getTodayDate);
  const [filterToDate, setFilterToDate] = useState(getTodayDate);
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterAccount, setFilterAccount] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [salesPage, setSalesPage] = useState(1);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const [salesRes, accountsRes, rtoOrders, dtoOrders] = await Promise.all([
        axios.get(SALES_API),
        axios.get(`${API_BASE}/accounts`).catch(() => ({ data: [] })),
        rtoAPI.getRtoOrders({ type: "RTO" }).catch(() => []),
        rtoAPI.getRtoOrders({ type: "DTO" }).catch(() => []),
      ]);

      setSales(Array.isArray(salesRes.data) ? salesRes.data : []);
      setAccounts(Array.isArray(accountsRes.data) ? accountsRes.data : []);
      setRtoRows([
        ...(Array.isArray(rtoOrders)
          ? rtoOrders.map((order, index) => buildRtoRow(order, "RTO", index))
          : []),
        ...(Array.isArray(dtoOrders)
          ? dtoOrders.map((order, index) => buildRtoRow(order, "DTO", index))
          : []),
      ]);
    } catch (error) {
      console.error("Total sales error:", error);
      setSales([]);
      setAccounts([]);
      setRtoRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const deliveredRows = useMemo(() => buildDeliveredSaleRows(sales), [sales]);

  const allRows = useMemo(
    () =>
      [...deliveredRows, ...rtoRows].sort(
        (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
      ),
    [deliveredRows, rtoRows]
  );

  const platformOptions = useMemo(
    () =>
      Array.from(
        new Set(allRows.map((row) => String(row.platform || "").trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [allRows]
  );

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return allRows.filter((row) => {
      if (!isDateInRange(row.date, filterFromDate, filterToDate)) return false;
      if (filterPlatform && String(row.platform || "") !== filterPlatform) return false;
      if (filterAccount && String(row.account || "") !== filterAccount) return false;

      if (!query) return true;

      return [
        formatDate(row.date),
        row.platform,
        row.account,
        row.product,
        row.unit,
        row.amount,
        row.status,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [allRows, filterAccount, filterFromDate, filterPlatform, filterToDate, searchQuery]);

  const displayRows = useMemo(() => {
    const grouped = new Map();

    for (const row of filteredRows) {
      const dateKey = getDateKey(row.date) || "-";
      const productKey = String(row.product || "-").trim().toLowerCase();
      const status = normalizeStatus(row.status);
      const key = `${dateKey}|${productKey}|${status}`;
      const existing = grouped.get(key);

      if (existing) {
        existing.platforms.push(row.platform);
        existing.accounts.push(row.account);
        existing.unit = Math.max(existing.unit, toNumber(row.unit));
        existing.amount += toNumber(row.amount);
        continue;
      }

      grouped.set(key, {
        id: key,
        date: row.date,
        platforms: [row.platform],
        accounts: [row.account],
        product: row.product || "-",
        unit: toNumber(row.unit),
        productUnit: toNumber(row.productUnit ?? row.unit),
        amount: toNumber(row.amount),
        status,
      });
    }

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        platform: joinUnique(row.platforms),
        account: joinUnique(row.accounts),
        unit: row.unit,
      }))
      .sort((a, b) => {
        const dateSort = new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
        if (dateSort !== 0) return dateSort;
        return String(a.product || "").localeCompare(String(b.product || ""));
      });
  }, [filteredRows]);

  useEffect(() => {
    setSalesPage(1);
  }, [filterAccount, filterFromDate, filterPlatform, filterToDate, searchQuery]);

  const salesPageCount = Math.max(1, Math.ceil(displayRows.length / TOTAL_SALES_PAGE_SIZE));
  const paginatedRows = displayRows.slice(
    (salesPage - 1) * TOTAL_SALES_PAGE_SIZE,
    salesPage * TOTAL_SALES_PAGE_SIZE
  );

  useEffect(() => {
    if (salesPage > salesPageCount) {
      setSalesPage(salesPageCount);
    }
  }, [salesPage, salesPageCount]);

  const summaryCards = useMemo(() => {
    const totalUnit = getTotalSalesUnit(displayRows);
    const totalAmount = displayRows.reduce((sum, row) => sum + toNumber(row.amount), 0);
    const totalReturn = displayRows
      .filter((row) => ["RTO", "DTO"].includes(row.status))
      .reduce((sum, row) => sum + toNumber(row.unit), 0);

    return [
      { label: "Total Unit", value: totalUnit, tone: "blue" },
      { label: "Total Amount", value: `Rs.${totalAmount.toFixed(2)}`, tone: "green" },
      { label: "Total Return", value: totalReturn, tone: "red" },
    ];
  }, [displayRows]);

  return (
    <div className="total-sales-page">
      <div className="total-sales-topbar">
        <div>
          <h2>Total Sales</h2>
        </div>

        <div className="total-sales-actions">
          <button type="button" className="total-sales-top-btn refresh" onClick={fetchRows}>
            Refresh
          </button>
          <button type="button" className="total-sales-top-btn back" onClick={() => navigate("/sales")}>
            Back To Sales
          </button>
        </div>
      </div>

      <div className="total-sales-summary-cards">
        {summaryCards.map((card) => (
          <div key={card.label} className={`total-sales-summary-card ${card.tone}`}>
            <span className="total-sales-summary-label">{card.label}</span>
            <span className="total-sales-summary-value">{card.value}</span>
          </div>
        ))}
      </div>

      <div className="total-sales-card">
        <div className="total-sales-filters">
          <input
            type="date"
            value={filterFromDate}
            onChange={(event) => setFilterFromDate(event.target.value)}
          />
          <input
            type="date"
            value={filterToDate}
            onChange={(event) => setFilterToDate(event.target.value)}
          />
          <select
            value={filterPlatform}
            onChange={(event) => setFilterPlatform(event.target.value)}
          >
            <option value="">All Platforms</option>
            {platformOptions.map((platform) => (
              <option key={platform} value={platform}>
                {platform}
              </option>
            ))}
          </select>
          <select
            value={filterAccount}
            onChange={(event) => setFilterAccount(event.target.value)}
          >
            <option value="">All Accounts</option>
            {accounts.map((account) => (
              <option key={account.id || account.account_name} value={account.account_name}>
                {account.account_name}
              </option>
            ))}
          </select>
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search all"
          />
          <button
            type="button"
            className="total-sales-reset-btn"
            onClick={() => {
              setFilterFromDate("");
              setFilterToDate("");
              setFilterPlatform("");
              setFilterAccount("");
              setSearchQuery("");
            }}
          >
            Reset
          </button>
        </div>

        <div className="total-sales-scroll">
          <table className="total-sales-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Platform</th>
                <th>Account</th>
                <th>Product Name</th>
                <th>Total Unit</th>
                <th>Total Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="7" className="total-sales-empty">
                    Loading total sales...
                  </td>
                </tr>
              )}

              {!loading &&
                paginatedRows.map((row, index) => (
                  <tr key={`${row.id || "total-sale"}-${(salesPage - 1) * TOTAL_SALES_PAGE_SIZE + index}`}>
                    <td className="total-sales-date-cell">{formatDate(row.date)}</td>
                    <td>{row.platform || "-"}</td>
                    <td>{row.account || "-"}</td>
                    <td className="total-sales-product-cell">{row.product || "-"}</td>
                    <td>{toNumber(row.unit)}</td>
                    <td className="total-sales-amount-cell">Rs.{toNumber(row.amount).toFixed(2)}</td>
                    <td>
                      <span className={getStatusClass(row.status)}>{row.status}</span>
                    </td>
                  </tr>
                ))}

              {!loading && displayRows.length === 0 && (
                <tr>
                  <td colSpan="7" className="total-sales-empty">
                    No total sales found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {displayRows.length > TOTAL_SALES_PAGE_SIZE && (
          <div className="total-sales-pagination">
            <button
              type="button"
              className="total-sales-pagination-btn"
              disabled={salesPage === 1}
              onClick={() => setSalesPage((page) => Math.max(1, page - 1))}
            >
              Prev
            </button>
            {Array.from({ length: salesPageCount }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                className={`total-sales-pagination-btn ${salesPage === page ? "active" : ""}`}
                onClick={() => setSalesPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              className="total-sales-pagination-btn"
              disabled={salesPage === salesPageCount}
              onClick={() => setSalesPage((page) => Math.min(salesPageCount, page + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
